<template>
  <div>
    <LoadingOverlay v-if="loading" />
    <div>
      <div id="view-sub-nav">
        <DateRangePicker default="empty" :short="true" @change="dateRangeChanged"></DateRangePicker>
        <v-spacer></v-spacer>
        <v-btn
          class="ml-3"
          :disabled="this.selected.length !== 1"
          variant="outlined"
          color="primary"
          @click="generateTaxJarTransaction"
          >Generate TaxJar Transaction</v-btn
        >
      </div>
      <!-- return-object: V3's v-model holds item values (item-value, default 'id'), V2's held the rows themselves -->
      <v-data-table
        v-model="selected"
        :items="missingTaxJar"
        :headers="headers"
        select-strategy="single"
        show-select
        return-object
      >
        <template #item.id="{ item }">
          {{ item.id || '-' }}
        </template>
        <template #item.orderNumber="{ item }">
          {{ item.orderNumber || '-' }}
        </template>
        <template #item.type="{ item }">
          {{ item.type }}
        </template>
        <template #item.customerEmail="{ item }">
          {{ item.customerEmail || '-' }}
        </template>
        <template #item.dateCreated="{ item }">
          {{ $utils.convertDate(item.dateCreated) || '-' }}
        </template>
        <template #item.paidTotal="{ item }">
          {{ $numeral(item.paidTotal).format('0,0.00') || '-' }}
        </template>
        <template #item.orderTotal="{ item }">
          {{ $numeral(item.orderTotal).format('0,0.00') || '-' }}
        </template>
        <template #item.salesTax="{ item }">
          <div
            class="text-center"
            :class="item.salesTax == undefined ? 'text-red' : ''"
            :style="item.salesTax == undefined ? 'border: 2px solid red;' : ''"
          >
            {{ item.salesTax != undefined ? $numeral(item.salesTax).format('0,0.00') : 'Missing Tax' }}
          </div>
        </template>
        <template #item.balanceDue="{ item }">
          {{ item.balanceDue == 0 ? 'Paid' : item.balanceDue }}
        </template>
        <template #item.actions="{ item }">
          <v-tooltip location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon size="small" @click="viewQuote(item)" v-bind="props">mdi-clipboard-text</v-icon>
            </template>
            <span>Original Quote</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </div>

    <div class="text-center">
      <v-snackbar color="bgGrey" v-model="responseSnackbar" vertical location="top" :timeout="-1">
        <div class="text-h6 pb-4">{{ responseMessage.message }}</div>
        <div v-if="responseMessage.taxJarTransactionId">
          <p class="text-body-1 font-weight-bold text-red">Please check TaxJar:</p>
          <p>TaxJar Transaction Order ID: {{ responseMessage.taxJarTransactionId }}</p>
          <p>TaxJar Transaction Date: {{ responseMessage.taxJarTransactionDate }}</p>
          <p>TaxJar Sales Tax: {{ responseMessage.taxJarSalesTax }}</p>
        </div>
        <div v-else-if="responseMessage.error">
          <p class="text-body-1 font-weight-bold text-red">Error:</p>
          <p>{{ responseMessage.error }}</p>
        </div>

        <template>
          <v-btn class="float-right" color="red" variant="text" @click="responseSnackbar = false"> Close </v-btn>
        </template>
      </v-snackbar>
    </div>

    <!-- Component Overlay Dialog -->
    <v-dialog v-model="quoteDialog" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="primary" class="no-print elevation-0">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              () => {
                quoteDialog = false
                overlayComponent = null
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <component
          :is="overlayComponent"
          :shipment="selectedOrder"
          :quoteID="selectedOrder.quoteID"
          :versionID="selectedOrder.versionID"
        ></component>
        <v-card-text> </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script type="text/javascript">
import QuoteEdit from '@/views/QuoteEdit'
import DateRangePicker from '@/components/ui/DateRangePicker'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import { db, functions } from '@/js/db'
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'TaxJar',
  components: {
    QuoteEdit,
    DateRangePicker,
    LoadingOverlay
  },
  data() {
    return {
      activeDateRange: null,
      selected: [],
      loading: false,
      missingTaxJar: [],
      headers: [
        {
          title: 'Order ID',
          sortable: false,
          key: 'id'
        },
        {
          title: 'Order #',
          sortable: false,
          key: 'orderNumber'
        },
        {
          title: 'Type',
          sortable: false,
          key: 'type'
        },
        {
          title: 'Customer Email',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Date Created',
          sortable: true,
          key: 'dateCreated'
        },
        {
          title: 'Paid Total $',
          sortable: false,
          key: 'paidTotal'
        },
        {
          title: 'Order Total $',
          sortable: false,
          key: 'orderTotal'
        },
        {
          title: 'Sales Tax $',
          sortable: false,
          key: 'salesTax'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          width: 160,
          key: 'actions'
        }
      ],
      selectedOrder: {},
      quoteDialog: false,
      dialogTitle: null,
      overlayComponent: null,
      responseSnackbar: false,
      responseMessage: {
        message: '',
        taxJarTransactionId: '',
        taxJarTransactionDate: '',
        taxJarSalesTax: '',
        error: ''
      }
    }
  },
  methods: {
    dateRangeChanged(range) {
      this.activeDateRange = range

      this.loadData()
    },
    async loadData() {
      this.loading = true
      this.selected = []
      this.missingTaxJar = [] // resets the array on reload

      // set orderSnap depending on date range
      const orderSnap = await getDocs(
        query(
          collection(db, 'orders'),
          where('foc', '==', false),
          where('region', '==', 'us'),
          where('dateCreated', '>=', this.activeDateRange[0].startOf('day').toDate()),
          where('dateCreated', '<=', this.activeDateRange[1].endOf('day').toDate())
        )
      )

      for (const order of orderSnap.docs) {
        // check if paid for and doesnt have taxJarOrder
        if (order.data().payments.length > 0 && !order.data()?.taxJarOrder) {
          let orderObj
          // checking if its shipped/dispatched
          if (order.data().type === 'sample-pack') {
            if (order.data().dispatched) {
              orderObj = { ...order.data(), id: order.id }
              this.missingTaxJar.push(orderObj)
              continue
            }
          } else if (order.data().type === 'mega-admin') {
            if (order.data().shipped) {
              orderObj = { ...order.data(), id: order.id }
              this.missingTaxJar.push(orderObj)
              continue
            }
          }
        }
      }

      // add cost/paid/balance info
      for (const orderObj of this.missingTaxJar) {
        // order total
        orderObj.orderTotal = orderObj.total
        // add paid total
        let paidTotal = 0
        for (const payment of orderObj.payments) {
          paidTotal += payment.amount
        }
        orderObj.paidTotal = paidTotal

        // calc order total
        if (orderObj.region === 'us') {
          // add sales tax
          const quoteSnap = await getDoc(doc(db, 'quotes', orderObj.quoteID))

          const version = quoteSnap.data().versions.find((version) => {
            return version.id === orderObj.versionID
          })
          if (version.pricing?.salesTax) {
            orderObj.salesTax = version.pricing?.salesTax?.amount_to_collect
              ? version.pricing?.salesTax?.amount_to_collect
              : version.pricing?.salesTax?.rate
          } else {
            orderObj.salesTax = undefined
          }
        }

        // add balance due
        if (orderObj.balanceOutstanding) {
          orderObj.balanceDue = orderObj.orderTotal.toFixed(2) - orderObj.paidTotal.toFixed(2)
        } else {
          orderObj.balanceDue = 0
        }
      }

      this.loading = false
    },
    async generateTaxJarTransaction() {
      try {
        this.loading = true
        let order = this.selected[0] // only one selection allowed
        let taxJarOrder
        if (order.region === 'us') {
          const orderSnap = await getDoc(doc(db, 'orders', order.id))

          let orderData = orderSnap.data()

          const quote = await getDoc(doc(db, 'quotes', orderData.quoteID))
          const version = quote.data().versions.find((version) => {
            return version.id === orderData.versionID
          })

          if (!version.pricing.salesTax) {
            this.loading = false
            this.responseMessage = {
              message: 'No sales tax found for this order',
              error: 'No sales tax found for this order'
            }
            return false
          }
          // Set transaction date
          const createTaxJarTransactionFromOrder = httpsCallable(functions, 'createTaxJarTransactionFromOrder')
          taxJarOrder = await createTaxJarTransactionFromOrder({
            id: order.id,
            payments: orderData.payments,
            customerContactDetails: orderData.customerContactDetails,
            pricing: version.pricing
          })

          if (taxJarOrder && !orderData.taxJarOrder) {
            await updateDoc(doc(db, 'orders', order.id), {
              taxJarOrder: taxJarOrder
            })
          }
        }
        this.responseMessage = {
          message: 'TaxJar transaction created successfully',
          taxJarTransactionId: taxJarOrder.data.order?.transaction_id || 'Not Found',
          taxJarTransactionDate: taxJarOrder.data.order?.transaction_date || 'Not Found',
          taxJarSalesTax: taxJarOrder.data.order?.sales_tax || 'Not Found'
        }
      } catch (error) {
        this.loading = false
        this.responseMessage = {
          message: 'Error creating TaxJar transaction',
          error: error.message
        }
        return false
      }
      this.loadData()
      this.loading = false
      this.responseSnackbar = true
      return true
    },
    async viewQuote(orderObj) {
      if (this.missingTaxJar.length > 0) {
        const orderDoc = await getDoc(doc(db, 'orders', orderObj.id))

        this.quoteDialog = true
        this.selectedOrder = orderDoc.data()
        this.dialogTitle = orderDoc.data().customerEmail
        this.overlayComponent = 'QuoteEdit'
      }
    }
  }
}
</script>
